import { Link } from "react-router-dom";

const SECTIONS = [
  { n: "1", title: "Acceptance of Terms", body: "By using the BARIQUE website, creating an account, or placing an order, you acknowledge that you have read, understood, and agreed to these Terms & Conditions. If you do not agree with any part of these Terms, please refrain from using our website." },
  { n: "2", title: "About BARIQUE", body: "BARIQUE is a designer fashion and couture brand offering luxury apparel, bridal wear, occasion wear, menswear, womenswear, accessories, and related fashion products through its online and offline channels." },
  { n: "3", title: "Product Information", body: "We strive to display product colors, fabrics, embroidery, and details as accurately as possible. However:", bullets: [
    "Actual colors may vary slightly due to screen settings and photography.",
    "Handmade and embroidered products may have slight variations that are characteristic of artisanal craftsmanship.",
    "Product descriptions, images, and prices are subject to change without prior notice.",
  ]},
  { n: "4", title: "Orders & Acceptance", bullets: [
    "All orders are subject to availability and acceptance by BARIQUE.",
    "We reserve the right to refuse or cancel any order at our discretion.",
    "An order confirmation email does not guarantee acceptance of the order.",
    "In case of inventory issues or unforeseen circumstances, we may cancel an order and issue a full refund.",
  ]},
  { n: "5", title: "Pricing & Payments", bullets: [
    "All prices are displayed in Indian Rupees (INR) unless otherwise specified.",
    "Applicable taxes will be charged as per Indian law.",
    "International customers may be responsible for customs duties, import taxes, and local charges applicable in their country.",
    "Payments must be completed through our approved payment gateways before order processing begins.",
  ]},
  { n: "6", title: "Custom & Made-to-Order Products", body: "Most BARIQUE products are made-to-order, customized, or handcrafted.", bullets: [
    "Measurements and customization details provided by customers are their sole responsibility.",
    "BARIQUE is not responsible for fitting issues resulting from incorrect measurements submitted by customers.",
    "Once production begins, customization requests may not be modified or cancelled.",
  ]},
  { n: "7", title: "Shipping & Delivery", bullets: [
    "Delivery timelines vary based on product type and customization requirements.",
    "Estimated delivery timelines are provided for reference only.",
    "Delays caused by logistics providers, weather conditions, customs clearance, or other factors beyond our control shall not make BARIQUE liable for compensation.",
    "Risk of loss transfers to the customer upon delivery.",
  ]},
  { n: "8", title: "Returns, Exchanges & Refunds", body: "Due to the bespoke and handcrafted nature of our products:", bullets: [
    "All customized, made-to-order, bridal, couture, sale, and discounted products are non-returnable, non-exchangeable, and non-refundable.",
    "Ready-to-wear products may be eligible for exchange only if approved by BARIQUE.",
    "Any manufacturing defect or incorrect product received must be reported within 48 hours of delivery with supporting photographs.",
    "Approved claims will be reviewed and resolved at BARIQUE's sole discretion.",
  ]},
  { n: "9", title: "Cancellation Policy", bullets: [
    "Orders cannot be cancelled once production or processing has commenced.",
    "If BARIQUE cancels an order due to product unavailability or operational reasons, a full refund will be issued.",
  ]},
  { n: "10", title: "Intellectual Property", body: "All content on the BARIQUE website, including but not limited to logos, designs, product images, graphics, text, videos, collections and website layout are the exclusive property of BARIQUE and are protected under applicable intellectual property laws. Unauthorized reproduction, copying, distribution, or commercial use is strictly prohibited." },
  { n: "11", title: "User Conduct", body: "Users agree not to:", bullets: [
    "Use the website for unlawful purposes.",
    "Upload malicious code or harmful content.",
    "Attempt unauthorized access to the website or its systems.",
    "Misrepresent personal information.",
    "Violate any applicable laws or regulations.",
  ]},
  { n: "12", title: "Privacy", body: "Any personal information collected through the website shall be handled in accordance with BARIQUE's Privacy Policy." },
  { n: "13", title: "Limitation of Liability", body: "To the fullest extent permitted by law, BARIQUE shall not be liable for:", bullets: [
    "Indirect or consequential losses.",
    "Loss of profits, business, or goodwill.",
    "Delays in delivery beyond our reasonable control.",
    "Technical interruptions, website downtime, or data loss.",
  ], footer: "Our maximum liability shall not exceed the amount paid by the customer for the product in question." },
  { n: "14", title: "Force Majeure", body: "BARIQUE shall not be held responsible for delays or failure to perform obligations due to circumstances beyond its reasonable control, including but not limited to natural disasters, pandemics, government restrictions, strikes, transportation disruptions, or acts of God." },
  { n: "15", title: "Governing Law", body: "These Terms & Conditions shall be governed by and interpreted in accordance with the laws of India. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of New Delhi, India." },
  { n: "16", title: "Changes to Terms", body: "BARIQUE reserves the right to modify these Terms & Conditions at any time without prior notice. Updated terms will be posted on the website and shall become effective immediately upon publication." },
];

export default function Terms() {
  return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen" data-testid="terms-page">
      <div className="container-lux max-w-4xl">
        <p className="overline text-muted-foreground">The Fine Print</p>
        <h1 className="mt-4 font-serif tracking-tighter text-5xl md:text-7xl font-light">Terms &amp; Conditions</h1>
        <p className="mt-8 text-sm text-muted-foreground leading-relaxed max-w-2xl">
          Welcome to BARIQUE. By accessing, browsing, or purchasing products from our website, you agree to be bound by these Terms &amp; Conditions. Please read them carefully before using our website or placing an order.
        </p>

        <div className="mt-16 space-y-14">
          {SECTIONS.map((s) => (
            <section key={s.n} data-testid={`terms-${s.n}`}>
              <div className="flex items-baseline gap-4 border-b border-border pb-4 mb-6">
                <span className="font-serif text-2xl text-muted-foreground">{s.n}.</span>
                <h2 className="font-serif text-2xl md:text-3xl tracking-tighter">{s.title}</h2>
              </div>
              {s.body && <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{s.body}</p>}
              {s.bullets && (
                <ul className="mt-4 space-y-2 pl-5 list-disc marker:text-muted-foreground/60">
                  {s.bullets.map((b, i) => <li key={i} className="text-sm text-muted-foreground leading-relaxed">{b}</li>)}
                </ul>
              )}
              {s.footer && <p className="text-sm text-muted-foreground leading-relaxed mt-4">{s.footer}</p>}
            </section>
          ))}

          <section data-testid="terms-17">
            <div className="flex items-baseline gap-4 border-b border-border pb-4 mb-6">
              <span className="font-serif text-2xl text-muted-foreground">17.</span>
              <h2 className="font-serif text-2xl md:text-3xl tracking-tighter">Contact Information</h2>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">For questions, concerns, or grievances, please contact:</p>
            <div className="mt-6 space-y-2">
              <p className="font-serif text-2xl tracking-tighter">BARIQUE</p>
              <p className="text-sm"><span className="text-muted-foreground">Email · </span><a href="mailto:bariquecouture@gmail.com" className="underline underline-offset-4 hover:text-ink">bariquecouture@gmail.com</a></p>
              <p className="text-sm"><span className="text-muted-foreground">Website · </span><a href="https://www.barique.in" target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-ink">www.barique.in</a></p>
            </div>
          </section>
        </div>

        <div className="mt-20 border-t border-border pt-10 text-xs text-muted-foreground leading-relaxed max-w-2xl">
          By using this website and placing an order, you acknowledge that you have read, understood, and agreed to these Terms &amp; Conditions.
        </div>

        <div className="mt-14">
          <Link to="/" className="lux-btn-outline">Return Home</Link>
        </div>
      </div>
    </div>
  );
}
